import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { HelpCircle, Bug, Lightbulb, MessageSquare, X } from 'lucide-react';
import WaWiSupportDrawer from './WaWiSupportDrawer';
import ScreenshotReporter from './ScreenshotReporter';
import { useDraggableEdge } from '../../hooks/useDraggableEdge';

export default function WaWiSupportButton() {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isReporterOpen, setIsReporterOpen] = useState(false);
  const [reporterType, setReporterType] = useState<'bug_report' | 'feature_request'>('bug_report');
  const menuRef = useRef<HTMLDivElement>(null);

  const { style, isDragging, wasDragged, handlePointerDown, menuPosition } = useDraggableEdge({
    storageKey: 'wawi_support_btn_pos',
  });

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsMenuOpen(false);
      }
    };
    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMenuOpen]);

  const handleButtonClick = () => {
    if (wasDragged) return;
    setIsMenuOpen(!isMenuOpen);
  };

  const handleBugReport = () => {
    setIsMenuOpen(false);
    setReporterType('bug_report');
    setIsReporterOpen(true);
  };

  const handleFeatureRequest = () => {
    setIsMenuOpen(false);
    setReporterType('feature_request');
    setIsReporterOpen(true);
  };

  const handleSupportTicket = () => {
    setIsMenuOpen(false);
    setIsDrawerOpen(true);
  };

  // Menu positioning classes based on button location
  const menuClasses = [
    'absolute w-56 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 py-1.5 animate-fade-in',
    menuPosition.vertical === 'top' ? 'bottom-14' : 'top-14',
    menuPosition.horizontal === 'left' ? 'right-0' : 'left-0',
  ].join(' ');

  return (
    <>
      <div ref={menuRef} id="bug-reporter-menu" className="screenshot-reporter-exclude" style={style}>
        {/* Context menu */}
        {isMenuOpen && (
          <div className={menuClasses}>
            <button
              onClick={handleBugReport}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            >
              <Bug className="w-4.5 h-4.5 text-red-500" />
              <div className="text-left">
                <div className="font-medium">{t('bugReporter.menuBug')}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('bugReporter.menuBugDesc')}</div>
              </div>
            </button>
            <button
              onClick={handleFeatureRequest}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-yellow-50 dark:hover:bg-yellow-900/20 transition-colors"
            >
              <Lightbulb className="w-4.5 h-4.5 text-yellow-500" />
              <div className="text-left">
                <div className="font-medium">{t('bugReporter.menuFeature')}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('bugReporter.menuFeatureDesc')}</div>
              </div>
            </button>
            <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
            <button
              onClick={handleSupportTicket}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            >
              <MessageSquare className="w-4.5 h-4.5 text-blue-500" />
              <div className="text-left">
                <div className="font-medium">{t('bugReporter.menuSupport')}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{t('bugReporter.menuSupportDesc')}</div>
              </div>
            </button>
          </div>
        )}

        {/* Floating button */}
        <button
          onPointerDown={handlePointerDown}
          onClick={handleButtonClick}
          className={`w-12 h-12 rounded-full shadow-lg flex items-center justify-center transition-all ${
            isDragging ? 'scale-110 opacity-80 cursor-grabbing' : 'hover:scale-105 cursor-grab'
          } ${isMenuOpen ? 'bg-gray-600 hover:bg-gray-700' : 'bg-primary-600 hover:bg-primary-700'} text-white`}
          title="Support"
        >
          {isMenuOpen ? <X className="w-5 h-5" /> : <HelpCircle className="w-6 h-6" />}
        </button>
      </div>

      <WaWiSupportDrawer isOpen={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} />
      <ScreenshotReporter
        isOpen={isReporterOpen}
        onClose={() => setIsReporterOpen(false)}
        defaultType={reporterType}
      />
    </>
  );
}
